import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import useTwoFactor from "../../hooks/useTwoFactor";
import Input from "Components/Common/Input/Input";
import Button from "Components/Common/Button/Button";
import "./Login.scss";
import "./TwoFactorAuth.scss";

const OTP_LENGTH = 6;

export default function TwoFactorAuth() {
  const [digits, setDigits] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const navigate = useNavigate();
  const {
    handleVerify,
    handleResend,
    loading,
    resending,
    error,
    resendSuccess,
    twoFactorToken,
  } = useTwoFactor();

  useEffect(() => {
    if (!twoFactorToken) {
      navigate("/", { replace: true });
    } else {
      inputRefs.current[0]?.focus();
    }
  }, [twoFactorToken, navigate]);

  const otp = digits.join("");

  const onSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    if (otp.length === OTP_LENGTH) {
      handleVerify(otp);
    }
  };

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        const next = [...digits];
        next[index] = "";
        setDigits(next);
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH);
    if (!pasted) return;
    const next = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => { next[i] = ch; });
    setDigits(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputRefs.current[focusIdx]?.focus();
  };

  return (
    <div className="loginWrapper">
      <div className="container">
        <div className="page">
          <section className="hero">
            <div className="hero-copy">
              <h1>Two-Factor Authentication</h1>
              <p>
                We've sent a one-time passcode to your registered contact.
                Enter it below to complete sign-in.
              </p>
            </div>
          </section>

          <section className="login-panel" aria-labelledby="2fa-heading">
            <h2 id="2fa-heading">Enter OTP</h2>
            <p>Check your registered mobile number for the 6-digit code.</p>

            <form onSubmit={onSubmit} noValidate>
              <div className="otp-boxes" onPaste={handlePaste}>
                {digits.map((digit, i) => (
                  <Input
                    key={i}
                    ref={(el: HTMLInputElement | null) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    disabled={loading}
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    aria-label={`Digit ${i + 1}`}
                    isError={!!error}
                    className={`otp-box${digit ? " otp-box--filled" : ""}`}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>

              {error && (
                <div className="form-error" role="alert" aria-live="polite">
                  {error}
                </div>
              )}

              {resendSuccess && (
                <div className="form-success" role="status">
                  OTP resent successfully.
                </div>
              )}

              <Button
                type="submit"
                className="login-btn"
                disabled={loading || otp.length < OTP_LENGTH}
                aria-busy={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner" aria-hidden="true" />
                    Verifying...
                  </>
                ) : (
                  "Verify"
                )}
              </Button>

              <p className="resend-row">
                Didn't receive the code?{" "}
                <Button
                  type="button"
                  className="resend-btn"
                  onClick={handleResend}
                  disabled={resending}
                >
                  {resending ? "Resending..." : "Resend OTP"}
                </Button>
              </p>
            </form>
          </section>
        </div>
      </div>
    </div>
  );
}
